import React, {createContext, useContext} from "react";
import {Tooltip, Typography} from "@mui/material";
import {OpenInNewOutlined} from "@mui/icons-material";
import {Blue3, Grey6, Grey17} from "../../theme/styles";

/**
 * A record's identifier, drawn the one way it is drawn everywhere in a project.
 *
 * ED, 2026-10-30: "Remove [Open the record]. Make the Object ID linkable and when clicked on it
 * shows the object detail and form data in a right side drawer. Next to the Object ID, put an icon
 * that indicates if clicked on it will open a new browser window showing the record. Let's do that
 * for all displayed Object IDs in the Project and its associated tabs."
 *
 * TWO DOORS ON ONE IDENTIFIER. The number opens the record drawer -- the facts and the form, beside
 * the project, the way the record page draws them -- and the icon beside it opens the full record
 * page in a NEW window, so the project is never left. Both take the REVISION, because /object/{usid}
 * names a revision and so does the drawer's read; a row that has no revision to hand (a record the
 * reader may not see) draws the number plain, with neither door.
 *
 * THE DRAWER IS THE PROJECT'S, not this component's: ProjectMembers mounts one RecordDrawer and
 * provides `RecordDrawerContext`, so every pane under it -- the pipeline board, the grids, the
 * timeline -- opens the same drawer without threading a callback through each.
 */
export const RecordDrawerContext = createContext<((revisionUSID: string) => void) | null>(null);

export function recordWindowPath(revisionUSID: string): string {
    return `/object/${revisionUSID}`;
}

export default function ObjectIdLink({id, revisionUSID, resolveRevision, size = 13, weight = 700}:
                                     {id?: string | null; revisionUSID?: string | null;
                                      /** For an identifier that knows only its LOGICAL object (a quote
                                       *  request's project, say): asked for the revision on the click.
                                       *  The window is opened on the gesture and pointed afterwards, so
                                       *  a popup blocker does not eat it. */
                                      resolveRevision?: () => Promise<string | null>;
                                      size?: number; weight?: number}) {
    const openDrawer = useContext(RecordDrawerContext);
    const reachable = !!revisionUSID || !!resolveRevision;
    const live = reachable && !!openDrawer;
    const revision = async () => revisionUSID ?? (resolveRevision ? await resolveRevision() : null);
    return (
        <span data-testid="object-id" style={{display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0}}>
            <Typography component="span"
                        sx={{fontSize: size, fontWeight: weight, color: live ? Blue3 : Grey17,
                             cursor: live ? "pointer" : "default", whiteSpace: "nowrap"}}
                        onClick={event => {
                            if (!live) return;
                            event.stopPropagation();
                            revision().then(found => { if (found) openDrawer!(found); }).catch(() => undefined);
                        }}>
                {id ?? "—"}
            </Typography>
            {reachable && (
                <Tooltip title={`Open ${id ?? "the record"} in a new window`} placement="top">
                    <OpenInNewOutlined data-testid="open-in-new"
                                       sx={{fontSize: Math.max(12, size - 1), color: Grey6, cursor: "pointer",
                                            "&:hover": {color: Blue3}}}
                                       onClick={event => {
                                           event.stopPropagation();
                                           // NO "noopener": the app keeps its sign-in in sessionStorage,
                                           // which a window opened with an opener relationship inherits
                                           // and one opened without does not -- so a noopener window
                                           // landed on the sign-in page (Ed, 2026-10-30). Same origin,
                                           // so the opener relationship carries no risk here.
                                           if (revisionUSID) {
                                               window.open(recordWindowPath(revisionUSID), "_blank");
                                               return;
                                           }
                                           const opened = window.open("", "_blank");
                                           revision().then(found => {
                                               if (found && opened) opened.location.href = recordWindowPath(found);
                                               else opened?.close();
                                           }).catch(() => opened?.close());
                                       }}/>
                </Tooltip>
            )}
        </span>
    );
}
